<template>
  <picture
    :class="b(modifiers)"
    :style="style"
  >
    <source
      v-for="(mediaSrcset, mediaQuery) in sources"
      :key="mediaQuery"
      :media="mediaQuery"
      :srcset="mediaSrcset"
    />
    <img
      :sizes="parsedSizes"
      :srcset="finalSrcset"
      :src="fallback"
      :alt="alt"
      :loading="loading"
      :width="width"
      :height="height"
      :decoding="decoding"
      :style="imageStyle"
      @load="onLoad"
      @error="onError"
    />
  </picture>
</template>

<script>
  import { BREAKPOINTS } from '@/setup/globals';

  /**
   * Renders a picture element with srcset or sources, depending on provided data.
   */
  export default {
    name: 'e-picture',

    // components: {},
    // mixins: [],

    props: {
      /**
       * Accepts a srcset string of comma separated sources with width value.
       *
       * `'large.jpg 800w, medium.jpg 600w, ...'`
       */
      srcset: {
        type: String,
        default: null,
      },

      /**
       * Accepts an Object of key/value pairs which represent media/srcset for sources.
       *
       * `{ <media>: <srcset>, ... }`
       */
      sources: {
        type: Object,
        default: null,
      },

      /**
       * The alternative text, which is displayed if the image can not be loaded.
       */
      alt: {
        type: String,
        required: true,
      },

      /**
       * The fallback image path.
       */
      fallback: {
        type: String,
        required: true,
      },

      /**
       * Allows to add a ratio keeper pseudo element, which will preserve the vertical space of the final image.
       *
       * e.g. `1` is a ratio of 1:1 (600px x 600px), `0.75` is a ratio of 0.75:1 (600px x 800px)
       */
      ratio: {
        type: [Number, String],
        default: null,
      },

      /**
       * Sizes definition for srcset use
       *
       * `{ <breakpoint (px value|short name)>: <minWidth>, ... }`
       */
      sizes: {
        type: Object,
        default: null,
      },

      /**
       * Defines if image is displayed as inline-block element.
       */
      inline: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to enable native lazy loading.
       *
       * For browser support @see https://caniuse.com/?search=load
       */
      loading: {
        type: String,
        default: 'lazy',
        validator(value) {
          return ['lazy', 'eager', 'auto'].includes(value);
        },
      },

      /**
       * Allows to move the image decoding off the main thread. This should be 'sync' or 'auto' for images above the fold.
       *
       * For browser support @see https://caniuse.com/mdn-html_elements_img_decoding
       */
      decoding: {
        type: String,
        default: 'async',
        validator: (value) => ['sync', 'async', 'auto'].includes(value),
      },

      /**
       * Allows to set the image width.
       */
      width: {
        type: [String, Number],
        default: null,
      },

      /**
       * Allows to set the image height.
       */
      height: {
        type: [String, Number],
        default: null,
      },

      /**
       * Show a placeholder image while the image is loading or is not available.
       */
      placeholder: {
        type: Boolean,
        default: false,
      },

      /**
       * Set vertical object-position.
       */
      verticalAnchor: {
        type: String,
        default: null,
        validator: (value) => ['top', 'center', 'bottom'].includes(value),
      },

      /**
       * Set horizontal object-position.
       */
      horizontalAnchor: {
        type: String,
        default: null,
        validator: (value) => ['left', 'center', 'right'].includes(value),
      },
    },

    emits: ['image-loaded'],

    data() {
      return {
        /**
         * @type {Boolean} - Becomes true if the image is loaded.
         */
        loaded: false,

        /**
         * @type {Boolean} - Becomes true if the image has an error.
         */
        error: false,
      };
    },

    computed: {
      /**
       * Returns an object of BEM modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          inline: this.inline,
          ratio: !!this.ratio,
          loaded: this.loaded,
          loading: this.loading,
          placeholder: this.placeholder && !this.loaded,
          error: this.error,
        };
      },

      /**
       * Calculates root element styles.
       *
       * @returns {Object}
       */
      style() {
        const { ratio } = this;

        return ratio ? { '--aspect-ratio': ratio } : null;
      },

      /**
       * Calculates image element styles.
       *
       * @returns {Object}
       */
      imageStyle() {
        const { horizontalAnchor, verticalAnchor } = this;
        const objectPosition =
          horizontalAnchor || verticalAnchor ? `${horizontalAnchor || '50%'} ${verticalAnchor || '50%'}` : null;
        const objectFit = objectPosition && 'cover'; // TODO: implement object-fit as property
        const height = objectFit && '100%';

        return {
          height,
          objectFit,
          objectPosition,
        };
      },

      /**
       * Converts sizes object to string.
       *
       * @returns {String|null}
       */
      parsedSizes() {
        const { sizes } = this;

        if (!sizes) {
          return null;
        }

        const mappedSizesBreakpoints = {};
        const fallback = ',100vw';

        return (
          Object.keys(sizes)
            .map((breakpoint) => {
              const key = Number.isNaN(BREAKPOINTS[breakpoint]) // The viewport could be 0, so we need to test the type.
                ? breakpoint
                : BREAKPOINTS[breakpoint];

              mappedSizesBreakpoints[key] = sizes[breakpoint];

              return key;
            })
            .filter(Boolean) // Removes 0!
            .sort((a, b) => (a < b ? 1 : -1))
            .map((breakpoint) => {
              const viewWidth = Math.ceil((mappedSizesBreakpoints[breakpoint] / breakpoint) * 100);

              return `(min-width: ${breakpoint}px) ${viewWidth}vw`;
            })
            .join(',') + fallback
        );
      },

      /**
       * Determines the final value for the srcset attribute of the image.
       *
       * @returns {String|null}
       */
      finalSrcset() {
        return this.srcset ?? null;
      },
    },
    // watch: {},

    // beforeCreate() {},
    created() {
      if (this.srcset && !this.sizes) {
        // eslint-disable-next-line no-console
        console.warn("No 'sizes' where defined while using 'srcset'.");
      }
    },
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Load event handler for the image element.
       * @deprecated TODO: @load will no longer work with vue3.
       */
      onLoad() {
        this.loaded = true;
        this.$emit('image-loaded');
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  // @use '@/setup/scss/mixins';
  // @use '@/setup/scss/extends' as *;
  // @use '@/setup/scss/functions';

  :root {
    --aspect-ratio: 1000; // Makes sure the variable is defined and creates a very small spacer
  }

  .e-picture {
    // Can be <picture> or <img>!
    display: block;
    max-width: 100%;
    height: auto;

    img {
      display: block;
      width: 100%;
      max-width: 100%;
      height: auto;
    }

    &__image {
      max-width: 100%;
    }

    &--loading-lazy img {
      opacity: 0;
      transition: opacity variables.$transition-duration--200;
    }

    &--loaded img[loading] {
      // Attribute selector was required to increase weight.
      opacity: 1;
    }

    &--inline {
      display: inline-block;

      img {
        display: inline-block;
      }
    }

    &--ratio {
      &::before {
        display: block;
        content: '';
        float: left;
        padding-top: calc(100% / var(--aspect-ratio));
      }

      &::after {
        display: table;
        content: '';
        clear: both;
      }
    }

    &--placeholder {
      background: url('../assets/placeholder/placeholder.svg') no-repeat center center variables.$color-grayscale--920;
      background-size: 30%;
    }
  }
</style>
